import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";

const formatCurrency = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "R$ 0,00";
  return parsed.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

export default function FreightToCombineWarningModal({
  open,
  freightValue = 17500,
  netValue = 16200,
  minimumAntt = 16796,
  onClose,
  onContinue,
  loading = false,
}) {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="freight-warning-title"
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1.5,
          boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      {/* Botão Fechar */}
      <IconButton
        aria-label="Fechar"
        disabled={loading}
        onClick={onClose}
        sx={{ position: "absolute", right: 16, top: 16, color: "#607286" }}
      >
        <CloseIcon />
      </IconButton>

      {/* Cabeçalho */}
      <DialogTitle
        id="freight-warning-title"
        sx={{
          alignItems: "center",
          color: "#081B33",
          display: "flex",
          fontWeight: 700,
          fontSize: "1.35rem",
          gap: 2,
          pt: 1,
          pr: 6,
        }}
      >
        {/* Ícone de Exclamação (Laranja) */}
        <Box
          sx={{
            alignItems: "center",
            backgroundColor: "#FDF0D5",
            borderRadius: "50%",
            display: "flex",
            height: 52,
            justifyContent: "center",
            width: 52,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: "#ED8936",
              borderRadius: "50%",
              color: "#FFFFFF",
              display: "flex",
              height: 32,
              justifyContent: "center",
              width: 32,
            }}
          >
            <PriorityHighIcon sx={{ fontSize: 22, fontWeight: "bold" }} />
          </Box>
        </Box>
        Anúncio será “A COMBINAR”
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Descrição Explicativa */}
        <Typography sx={{ color: "#4B5B70", fontSize: "0.95rem", lineHeight: 1.5, mb: 1.5 }}>
          O valor líquido ficou abaixo do mínimo ANTT de{" "}
          <strong style={{ color: "#081B33" }}>{formatCurrency(minimumAntt)}</strong>.
        </Typography>

        <Typography sx={{ color: "#4B5B70", fontSize: "0.95rem", lineHeight: 1.5, mb: 3 }}>
          Para <strong>suas transportadoras</strong>, o frete será publicado pelo valor anunciado. Para <strong>autônomos</strong>, será exibido como “A COMBINAR”.
        </Typography>

        {/* Card de Valores (3 colunas) */}
        <Box
          sx={{
            backgroundColor: "#FFF8F0",
            borderRadius: 2.5,
            display: "grid",
            gap: { xs: 2, sm: 0 },
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1.2fr 1fr" },
            p: 2.5,
            alignItems: "center",
          }}
        >
          {/* Valor do frete */}
          <Box sx={{ pr: { sm: 1.5 } }}>
            <Typography sx={{ color: "#5A6B82", fontSize: 13, mb: 0.5 }}>
              Valor do frete
            </Typography>
            <Typography sx={{ color: "#081B33", fontSize: 18, fontWeight: 700 }}>
              {formatCurrency(freightValue)}
            </Typography>
          </Box>

          {/* Valor líquido (autônomo) */}
          <Box
            sx={{
              borderLeft: { sm: "1px solid #EAE0D5" },
              px: { sm: 2 },
              borderTop: { xs: "1px solid #EAE0D5", sm: "none" },
              pt: { xs: 1.5, sm: 0 },
            }}
          >
            <Typography sx={{ color: "#5A6B82", fontSize: 13, mb: 0.5 }}>
              Valor líquido (autônomo)
            </Typography>
            <Typography sx={{ color: "#D96B00", fontSize: 18, fontWeight: 700 }}>
              {formatCurrency(netValue)}
            </Typography>
          </Box>

          {/* Mínimo ANTT */}
          <Box
            sx={{
              borderLeft: { sm: "1px solid #EAE0D5" },
              pl: { sm: 2 },
              borderTop: { xs: "1px solid #EAE0D5", sm: "none" },
              pt: { xs: 1.5, sm: 0 },
            }}
          >
            <Typography sx={{ color: "#5A6B82", fontSize: 13, mb: 0.5 }}>
              Mínimo ANTT
            </Typography>
            <Typography sx={{ color: "#081B33", fontSize: 18, fontWeight: 700 }}>
              {formatCurrency(minimumAntt)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      {/* Ações */}
      <DialogActions sx={{ gap: 2, px: 3, pb: 2, pt: 1 }}>
        <Button
          fullWidth
          variant="outlined"
          disabled={loading}
          onClick={onClose}
          sx={{
            borderColor: "#D8E0E8",
            color: "#081B33",
            fontWeight: 600,
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            "&:hover": {
              borderColor: "#B0BEC5",
              backgroundColor: "#F5F7FA",
            },
          }}
        >
          Voltar
        </Button>

        <Button
          fullWidth
          variant="contained"
          disabled={loading}
          onClick={onContinue}
          sx={{
            backgroundColor: "#031D38",
            color: "#FFFFFF",
            fontWeight: 600,
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            "&:hover": {
              backgroundColor: "#0A294D",
            },
          }}
        >
          Publicar frete
        </Button>
      </DialogActions>
    </Dialog>
  );
}